import React, { useState } from 'react';
import { Mail, User, MessageSquare, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { mockData } from '../mock';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { contact } = mockData;
  const { toast } = useToast();
  
  // --- ESTADOS DE CONTROLE ---
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // --- ENVIO DIRETO PARA O FORMSPREE ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Endpoint extraído da sua configuração do Formspree
      const response = await fetch("https://formspree.io/f/mgopygld", {
        method: "POST",
        body: JSON.stringify(formData),
        headers: { 
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setIsSuccess(true);
        setFormData({ name: '', email: '', message: '' });
        toast({
          title: "Mensagem enviada!",
          description: "Entraremos em contato em breve.",
        });
      } else {
        const errorData = await response.json();
        toast({
          variant: "destructive",
          title: "Erro ao enviar",
          description: errorData.errors ? errorData.errors[0].message : "Tente novamente mais tarde.",
        });
      }
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Falha na conexão",
        description: "Verifique sua internet antes de tentar novamente.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contato" className="py-24 bg-[#0f1115] relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-orange-500/10 via-transparent to-transparent pointer-events-none"></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
            {contact.title}
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            {contact.subtitle}
          </p>
        </div>

        <div className="bg-white/[0.03] backdrop-blur-xl rounded-3xl p-8 md:p-12 shadow-2xl border border-white/10 min-h-[450px] flex flex-col justify-center transition-all duration-500">
          
          {isSuccess ? (
            // --- MENSAGEM DE SUCESSO (Renderizada no lugar do form) ---
            <div className="text-center animate-in fade-in zoom-in duration-500">
              <div className="flex justify-center mb-6">
                <div className="bg-green-500/20 p-6 rounded-full border border-green-500/30">
                  <CheckCircle2 size={64} className="text-green-500" />
                </div>
              </div>
              <h3 className="text-3xl font-black text-white mb-4 italic">Mensagem Enviada!</h3>
              <p className="text-gray-400 text-lg max-w-sm mx-auto">
                Agradecemos o seu contato. Nossa equipe analisará sua mensagem e responderá o mais rápido possível.
              </p>
              <Button 
                variant="ghost" 
                onClick={() => setIsSuccess(false)}
                className="mt-10 text-orange-500 hover:text-orange-400 hover:bg-orange-500/10 font-bold"
              >
                Enviar outra mensagem
              </Button>
            </div>
          ) : (
            // --- FORMULÁRIO ORIGINAL ---
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium text-gray-300 ml-1">Nome</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User size={18} className="text-gray-500" />
                  </div>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Seu nome completo"
                    className="pl-11 bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-orange-500/50 focus:ring-orange-500/20 h-14 rounded-xl transition-all"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-gray-300 ml-1">E-mail</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail size={18} className="text-gray-500" />
                  </div>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="seu@email.com"
                    className="pl-11 bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-orange-500/50 focus:ring-orange-500/20 h-14 rounded-xl transition-all"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="message" className="text-sm font-medium text-gray-300 ml-1">Mensagem</label>
                <div className="relative">
                  <div className="absolute top-4 left-4 pointer-events-none">
                    <MessageSquare size={18} className="text-gray-500" />
                  </div>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Como podemos ajudar?"
                    rows={5}
                    className="pl-11 bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-orange-500/50 focus:ring-orange-500/20 resize-none rounded-xl transition-all pt-4"
                  />
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white font-black text-xl py-8 shadow-xl shadow-orange-500/20 transition-all transform hover:-translate-y-1 active:scale-95 rounded-xl flex items-center justify-center gap-3"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin" size={24} />
                    <span>Enviando...</span>
                  </>
                ) : (
                  <>
                    <Send size={22} />
                    <span>Enviar Mensagem</span>
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default Contact;